import { jsx as _jsx } from "react/jsx-runtime";
/**
 * `<Can>` — `@casl/react`'s component re-exported and narrowed to the
 * app's `(Action, Subject)` tuples.
 *
 * Per FD `frontend-components.md § <Can>`. v7 of `@casl/react` ships a
 * single internal ability context populated by its own `AbilityProvider`;
 * `<WorkspaceProvider>` (in `./workspace-provider.tsx`) mounts that
 * provider with the ability built from the current principal so any
 * `<Can>` rendered below it picks the same ability instance up.
 *
 * Example:
 *
 *   <Can I="invite" a="Member">
 *     <Button onClick={openInviteModal}>Invite teammate</Button>
 *   </Can>
 *
 * Outside `<WorkspaceProvider>` `<Can>` receives no ability and renders
 * nothing — matching FD R2's "don't leak the existence of resources the
 * user isn't a member of" rule.
 */
import { AbilityProvider as CaslAbilityProvider, Can as CaslCan } from "@casl/react";
// CASL's Can has heavy overloaded generics that don't unify cleanly with a
// narrowed (Action, Subject) prop type without a forwarding wrapper. We cast
// it down to a permissive shape locally; consumers see only the narrow
// `CanProps` surface.
const ForwardCan = CaslCan;
/**
 * Typed `<Can>` wrapper. Forwards verbatim to `@casl/react`'s component;
 * narrowing the prop types here gives editors autocomplete for our
 * specific `Action` and `Subject` unions and rejects typos at compile time.
 */
export function Can(props) {
    return _jsx(ForwardCan, { ...props });
}
/**
 * Internal — `@casl/react`'s `<AbilityProvider>` with the `AppAbility`
 * generic pre-applied so `<WorkspaceProvider>` can mount it without
 * re-declaring the type.
 *
 * @internal
 */
export function AbilityProvider({ ability, children, }) {
    return _jsx(CaslAbilityProvider, { value: ability, children: children });
}
//# sourceMappingURL=can.js.map